import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import CommonTable from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import Loaders from "components/Common/Loaders";
import { AsyncClientSelect, StaticFilterSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import CandidateOnboardingViewModal from "pages/AllCommonModal/CandidateOnboardingViewModal/candidate-onboarding-view-modal";
import {
    fetchCandidateOnboardingMetadata,
    fetchCandidateOnboardingStatusOptions,
    submitCandidateOnboardingFilterData,
} from "model/CandidateOnboardingModel";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const ALL_STATUS_OPTION = { value: "", label: "All" };

const TWO_LINE_HEADER_ACCESSORS = new Set([
    "candidatename",
    "onboardingstatus",
    "documentsubmitted",
    "documentssubmitted",
]);

const normalizeColumnAccessor = (accessor = "") =>
    String(accessor ?? "").trim().toLowerCase().replace(/_/g, "");

const shouldUseTwoLineHeader = (title = "", accessor = "") => {
    const normalizedAccessor = normalizeColumnAccessor(accessor);
    const normalizedTitle = String(title ?? "").trim().toLowerCase();

    if (TWO_LINE_HEADER_ACCESSORS.has(normalizedAccessor)) {
        return true;
    }

    return (
        /^document\s+submitted$/.test(normalizedTitle) ||
        /^documents\s+submitted$/.test(normalizedTitle) ||
        /^candidate\s+name$/.test(normalizedTitle) ||
        /^onboarding\s+status$/.test(normalizedTitle) ||
        /^name\s+document\s+submitted$/.test(normalizedTitle)
    );
};

const formatCandidateOnboardingHeader = (title = "", accessor = "") => {
    const normalizedTitle = String(title ?? "").trim();
    const normalizedAccessor = normalizeColumnAccessor(accessor);

    if (!shouldUseTwoLineHeader(normalizedTitle, accessor)) {
        return normalizedTitle || title;
    }

    if (
        normalizedAccessor === "onboardingstatus" ||
        /^onboarding\s+status$/i.test(normalizedTitle)
    ) {
        return (
            <>
                Onboarding
                <br />
                Status
            </>
        );
    }

    if (
        normalizedAccessor === "documentsubmitted" ||
        normalizedAccessor === "documentssubmitted" ||
        /^document\s+submitted$/i.test(normalizedTitle) ||
        /^documents\s+submitted$/i.test(normalizedTitle)
    ) {
        return (
            <>
                Document
                <br />
                Submitted
            </>
        );
    }

    if (/^name\s+document\s+submitted$/i.test(normalizedTitle)) {
        return (
            <>
                Name Document
                <br />
                Submitted
            </>
        );
    }

    if (
        normalizedAccessor === "candidatename" ||
        /^candidate\s+name$/i.test(normalizedTitle)
    ) {
        return (
            <>
                Candidate
                <br />
                Name
            </>
        );
    }

    const words = normalizedTitle.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
        const midpoint = Math.ceil(words.length / 2);
        return (
            <>
                {words.slice(0, midpoint).join(" ")}
                <br />
                {words.slice(midpoint).join(" ")}
            </>
        );
    }

    return normalizedTitle || title;
};

const CandidateOnboarding = () => {
    const { permissionData } = usePermissionContext() || {};
    const { pageSize: settingsPageSize, settingsReady } =
        useCommonTableSettingsPageSize("CandidateOnboarding");
    const initialFilterFetchedRef = useRef(false);
    const [filterForm, setFilterForm] = useState({
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
        onboardingStatus: "",
        fromDate: "",
        toDate: "",
    });
    const [onboardingStatusOptions, setOnboardingStatusOptions] = useState([ALL_STATUS_OPTION]);
    const [onboardingData, setOnboardingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableMetaData, setTableMetaData] = useState([]);
    const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const filteredData = useMemo(() => onboardingData, [onboardingData]);

    const candidateOnboardingActionPermissions = useMemo(
        () => ({
            canViewDocument: Boolean(permissionData?.CandidateOnboardingListAccess),
        }),
        [permissionData]
    );

    const hasAnyCandidateOnboardingRowAction = useMemo(
        () => candidateOnboardingActionPermissions.canViewDocument,
        [candidateOnboardingActionPermissions]
    );

    const loadMetadata = async () => {
        const response = await fetchCandidateOnboardingMetadata();
        const columns = response?.data?.table?.columns || response?.table?.columns || [];
        if (Array.isArray(columns) && columns.length) {
            setTableMetaData(
                columns.map((col) => {
                    const accessor = pascalToCamel(col?.field) || col?.field;
                    const headerWrap = shouldUseTwoLineHeader(col?.title, accessor);

                    return {
                        Header: formatCandidateOnboardingHeader(col?.title, accessor),
                        exportHeader: col?.title || accessor,
                        accessor,
                        type: col?.type,
                        headerWrap,
                    };
                })
            );
        }
    };

    const loadOnboardingStatusOptions = async () => {
        const options = await fetchCandidateOnboardingStatusOptions();
        setOnboardingStatusOptions([
            ALL_STATUS_OPTION,
            ...(Array.isArray(options) ? options : []),
        ]);
    };

    useEffect(() => {
        setLoading(true);
        loadMetadata();
        loadOnboardingStatusOptions();
    }, []);

    /** 1) settings page size ready → 2) call filter API once */
    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getCandidateOnboardingList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    const buildFilterPayload = (overrides = {}) => ({
        ...requestPayload,
        pageNumber: overrides.pageNumber ?? requestPayload.pageNumber ?? 1,
        pageSize: overrides.pageSize ?? requestPayload.pageSize ?? DEFAULT_PAGE_SIZE,
        clientId: strOrNull(filterForm?.clientId),
        onboardingStatus: strOrNull(filterForm?.onboardingStatus),
        dateFrom: filterForm?.fromDate
            ? dayjs(filterForm.fromDate).startOf("day").toISOString()
            : null,
        dateTo: filterForm?.toDate
            ? dayjs(filterForm.toDate).endOf("day").toISOString()
            : null,
        ...overrides,
    });

    const getCandidateOnboardingList = async (payloadOverrides = {}) => {
        setLoading(true);
        try {
            const payload = buildFilterPayload(payloadOverrides);
            const response = await submitCandidateOnboardingFilterData(payload);
            const responseData = response?.data ?? response ?? {};
            const listData = Array.isArray(responseData?.records)
                ? responseData.records
                : Array.isArray(responseData?.data)
                    ? responseData.data
                    : Array.isArray(responseData)
                        ? responseData
                        : [];

            const mappedData = (Array.isArray(listData) ? listData : []).map((item = {}) => ({
                ...item,
                id: item?.id ?? item?._id ?? item?.candidateOnboardingId ?? "",
            }));

            if (response == null || response?.success === false) {
                setOnboardingData([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setOnboardingData(mappedData);
            setPaginationMeta({
                pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: Number(responseData?.totalPages) || 0,
                totalRecords: Number(responseData?.totalRecords) || mappedData.length,
            });
            setRequestPayload(payload);
        } catch (error) {
            toast.error(error?.message || "Unable to load candidate onboarding");
            setOnboardingData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleOnboardingStatusChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            onboardingStatus: option?.value ?? "",
        }));
    };

    const handleSearch = () => {
        getCandidateOnboardingList({ pageNumber: 1 });
    };

    const handleOnboardingDocument = (row = {}) => {
        setSelectedRow(row);
        setViewModalOpen(true);
    };

    const toggleViewModal = () => {
        setViewModalOpen((previous) => {
            if (previous) {
                setSelectedRow(null);
            }
            return !previous;
        });
    };

    const handlePageChange = async (page) => {
        await getCandidateOnboardingList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getCandidateOnboardingList({ pageNumber: 1, pageSize: rows });
    };

    return (
        <React.Fragment>
            <div className="page-content candidate-onboarding-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Candidate Onboarding</h5>
                            <hr />

                            <LegacyFilterToolbar>
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="4" className="metadata-filter-field metadata-filter-field--autocomplete">
                                        <span className="manage-user-filter-label">Select Facility</span>
                                        <div className="metadata-filter-field__control">
                                        <AsyncClientSelect
                                            key={`candidate-onboarding-client-${filterForm?.clientId || ""}`}
                                            pageName="CandidateOnboardingList"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyClientAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select Facility"
                                        />
                                        </div>
                                    </Col>
                                    <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--dropdown">
                                        <span className="manage-user-filter-label">Onboarding Status</span>
                                        <div className="metadata-filter-field__control">
                                        <StaticFilterSelect
                                            options={onboardingStatusOptions}
                                            placeholder="Onboarding Status"
                                            value={filterForm?.onboardingStatus ?? ""}
                                            onChange={handleOnboardingStatusChange}
                                            isClearable={false}
                                            isSearchable
                                        />
                                        </div>
                                    </Col>
                                    <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                                        <span className="manage-user-filter-label">Start Date</span>
                                        <div className="metadata-filter-field__control">
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={LEGACY_FILTER_INPUT_CLASS}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        </div>
                                    </Col>
                                    <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                                        <span className="manage-user-filter-label">End Date</span>
                                        <div className="metadata-filter-field__control">
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={LEGACY_FILTER_INPUT_CLASS}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        </div>
                                    </Col>
                                    <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <div className="metadata-filter-toolbar__actions">
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={handleSearch}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : tableMetaData?.length ? (
                                <CommonTable
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyCandidateOnboardingRowAction}
                                    isEditAccess={false}
                                    isViewAccess={false}
                                    isCheckboxLeftSide={false}
                                    isPagination
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    onOnboardingDocument={
                                        candidateOnboardingActionPermissions.canViewDocument
                                            ? handleOnboardingDocument
                                            : undefined
                                    }
                                    pageName="CandidateOnboarding"
                                />
                            ) : (
                                <div className="text-center text-muted py-4">No table metadata found</div>
                            )}
                        </CardBody>
                    </Card>

                    <CandidateOnboardingViewModal
                        isOpen={viewModalOpen}
                        toggle={toggleViewModal}
                        selectedRow={selectedRow}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

CandidateOnboarding.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(CandidateOnboarding);
